import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllCourses } from '../services/courseService';
import { getAllCategories } from '../services/categoryService';
import { resolveMediaUrl } from '../utils/media';
import './AllCourses.css';

function AllCourses() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch courses and categories
      const [coursesResponse, categoriesResponse] = await Promise.all([
        getAllCourses({ isActive: true }),
        getAllCategories({ isActive: true, sortBy: 'order', sortOrder: 'asc' })
      ]);

      if (coursesResponse.success) {
        setCourses(coursesResponse.data);
      }

      if (categoriesResponse.success) {
        setCategories([
          { _id: 'all', name: 'All Courses', slug: 'all' },
          ...categoriesResponse.data
        ]);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = courses
    .filter(course => activeCategory === 'all' || course.category?._id === activeCategory)
    .filter(course => 
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.instructor.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.category?.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

  const getCategoryColor = (category) => {
    return category?.color || '#667eea';
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getCourseCount = (categoryId) => {
    if (categoryId === 'all') return courses.length;
    return courses.filter(c => c.category?._id === categoryId).length;
  };

  const getCourseImage = (course) => resolveMediaUrl(course.image);

  if (loading) {
    return (
      <div className="all-courses-page">
        <div className="page-header">
          <div className="container">
            <h1 className="page-title">All Courses</h1>
            <p className="page-subtitle">Loading courses...</p>
          </div>
        </div>
        <div className="container">
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="all-courses-page">
        <div className="page-header">
          <div className="container">
            <button className="back-btn" onClick={() => navigate('/')}>
              ← Back to Home
            </button>
            <h1 className="page-title">All Courses</h1>
            <p className="page-subtitle error-message">{error}</p>
            <button className="retry-btn" onClick={fetchData}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="all-courses-page">
      <div className="page-header">
        <div className="container">
          <button className="back-btn" onClick={() => navigate('/')}>
            ← Back to Home
          </button>
          <h1 className="page-title">All Courses</h1>
          <p className="page-subtitle">
            Explore our complete collection of {courses.length}+ courses
          </p>
          
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search courses by title, instructor, or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button 
                className="clear-search" 
                onClick={() => setSearchQuery('')}
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="container">
        {categories.length > 0 && (
          <div className="category-filter">
            {categories.map(category => (
              <button
                key={category._id}
                className={`filter-btn ${activeCategory === category._id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category._id)}
                style={activeCategory === category._id ? {
                  backgroundColor: category.color || '#667eea',
                  borderColor: category.color || '#667eea'
                } : {}}
              >
                {category.icon && <span className="category-icon">{category.icon}</span>}
                {category.name}
                <span className="count">{getCourseCount(category._id)}</span>
              </button>
            ))}
          </div>
        )}

        <div className="results-info">
          <p>
            Showing {filteredCourses.length} course{filteredCourses.length !== 1 ? 's' : ''}
            {searchQuery && ` for "${searchQuery}"`}
          </p>
        </div>

        <div className="courses-grid">
          {filteredCourses.length > 0 ? (
            filteredCourses.map(course => (
              <div key={course._id} className="course-card" onClick={() => navigate(`/courses/${course.path}`)} style={{ cursor: 'pointer' }}>
                <div 
                  className="course-image" 
                  style={{ 
                    background: `linear-gradient(135deg, ${getCategoryColor(course.category)} 0%, ${getCategoryColor(course.category)}dd 100%)` 
                  }}
                >
                  {getCourseImage(course) ? (
                    <img
                      src={getCourseImage(course)}
                      alt={course.title}
                      onError={(event) => {
                        event.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <span className="course-emoji">{course.category?.icon || '📚'}</span>
                  )}
                  <div className="course-badge">{course.level}</div>
                </div>
                
                <div className="course-content">
                  <div className="course-meta">
                    <span className="course-category">
                      {course.category?.name || 'General'}
                    </span>
                    <div className="course-rating">
                      <span className="star">⭐</span>
                      <span className="rating-value">{course.rating || 0}</span>
                    </div>
                  </div>

                  <h3 className="course-title">{course.title}</h3>
                  
                  <div className="course-instructor">
                    <div className="instructor-avatar">
                      {course.instructor?.charAt(0) || 'I'}
                    </div>
                    <span className="instructor-name">{course.instructor}</span>
                  </div>

                  <div className="course-stats">
                    <div className="stat">
                      <span className="stat-icon">⏱️</span>
                      <span className="stat-value">{course.duration}</span>
                    </div>
                  </div>

                  <div className="course-footer">
                    <div className="course-price">
                      {formatPrice(course.price)}
                      {course.originalPrice && course.originalPrice > course.price && (
                        <span className="original-price">
                          {formatPrice(course.originalPrice)}
                        </span>
                      )}
                    </div>
                    <button className="enroll-btn" onClick={(e) => { e.stopPropagation(); navigate(`/courses/${course.path}`); }}>View Course</button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-results">
              <span className="no-results-icon">🔍</span>
              <h3>No courses found</h3>
              <p>Try adjusting your search or filter criteria</p>
              <button className="clear-filters-btn" onClick={() => {
                setSearchQuery('');
                setActiveCategory('all');
              }}>
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AllCourses;
